import React from "react";
import { Activity, Beaker, TrendingUp } from "lucide-react";
import { fmtDate, BPCategoryBadge, LipidStat } from "./shared";
import DiagnosticsSuggestion from "@/components/DiagnosticsSuggestion";

export default function OverviewTab({ latestBp, latestLipid, bpCount, lipidCount, onQuickJump }) {
  return (
    <div className="space-y-4" data-testid="cardio-overview">
      <DiagnosticsSuggestion specialty="cardiology" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Latest BP */}
      <button
        onClick={() => onQuickJump("bp")}
        data-testid="cardio-overview-bp-card"
        className="text-left ros-card hover:border-[#F87171]/40 transition-colors"
      >
        <div className="flex items-center gap-2 ros-label"><Activity size={12} /> Latest blood pressure</div>
        {latestBp ? (
          <>
            <div className="mt-3 text-4xl font-heading font-bold text-white">
              {latestBp.systolic}<span className="text-[#A3ACA7] text-2xl">/</span>{latestBp.diastolic}
              <span className="text-lg text-[#A3ACA7] ml-2">mmHg</span>
            </div>
            <div className="mt-3">
              <BPCategoryBadge category={latestBp.category} />
            </div>
            <div className="text-xs text-[#7A8480] mt-3">
              Measured {fmtDate(latestBp.date)} · {bpCount} readings on file
            </div>
          </>
        ) : (
          <div className="mt-4 text-sm text-[#A3ACA7]">No BP readings yet — add your first from the BP tab.</div>
        )}
      </button>

      {/* Latest Lipid */}
      <button
        onClick={() => onQuickJump("lipids")}
        data-testid="cardio-overview-lipid-card"
        className="text-left ros-card hover:border-[#F87171]/40 transition-colors"
      >
        <div className="flex items-center gap-2 ros-label"><Beaker size={12} /> Latest lipid panel</div>
        {latestLipid ? (
          <>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <LipidStat label="LDL"  value={latestLipid.ldl_mg_dl}  target="< 100" flagged={latestLipid.flags?.ldl_high} />
              <LipidStat label="HDL"  value={latestLipid.hdl_mg_dl}  target="> 40"  flagged={latestLipid.flags?.hdl_low} inverted />
              <LipidStat label="Trig" value={latestLipid.triglycerides_mg_dl} target="< 150" flagged={latestLipid.flags?.trig_high} />
              <LipidStat label="Non-HDL" value={latestLipid.non_hdl_mg_dl} target="< 130" flagged={(latestLipid.non_hdl_mg_dl || 0) > 130} />
            </div>
            <div className="text-xs text-[#7A8480] mt-3">
              Panel dated {fmtDate(latestLipid.date)} · {lipidCount} panels on file
            </div>
          </>
        ) : (
          <div className="mt-4 text-sm text-[#A3ACA7]">No lipid panels yet — add one from the Lipids tab.</div>
        )}
      </button>

      {/* Score CTA */}
      <button
        onClick={() => onQuickJump("risk")}
        data-testid="cardio-overview-risk-card"
        className="md:col-span-2 text-left ros-card hover:border-[#F87171]/40 transition-colors"
      >
        <div className="flex items-center gap-2 ros-label"><TrendingUp size={12} /> Cardiovascular risk scoring</div>
        <div className="mt-3 font-heading text-lg text-white">
          Run ASCVD 10-year or HEART chest-pain score →
        </div>
        <div className="text-xs text-[#A3ACA7] mt-1.5 max-w-2xl">
          ASCVD Pooled Cohort Equations estimate 10-year atherosclerotic risk from age, sex, cholesterol, BP,
          smoking, and diabetes. HEART Score stratifies chest-pain patients for ED disposition.
        </div>
      </button>
      </div>
    </div>
  );
}
